import { useState, useRef } from 'react';
import { log } from '../utils/protokoll';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kundenApi, standorteApi, gebaeudeApi, stockwerkeApi, elementeApi, projekteApi, pruefbereicheApi, Pruefbereich } from '../services/api';
import { ProjektWithDetails } from '../types/projekte';
import {
  DoorOpen,
  Users,
  MapPin,
  Layers,
  ChevronRight,
  PlusCircle,
  ArrowLeft,
  Search,
  CheckCircle2,
  Trash2,
  Info,
  Upload,
  Download,
  Plus,
  X,
  FileSpreadsheet,
  AlertCircle,
  ShieldCheck,
  FolderKanban,
  Flame,
  Wind,
  Bell,
  Droplets
} from 'lucide-react';
import DoorCaptureWizard from '../components/DoorCaptureWizard';
import GenericElementCaptureWizard from '../components/GenericElementCaptureWizard';
import { ElementTyp, TYP_LABELS, schluesselFuerElementTyp } from '../types/elemente';

/**
 * Bereiche, die ihr eigenes, spezialisiertes Erfassungsformular haben
 * (DoorCaptureWizard.tsx) statt des generischen Formulars (Phase 6,
 * Luecke 2). Bewusst nur TÜR: DoorCaptureWizard erzeugt aktuell immer
 * `typ: 'TÜR'} -- SCHOTT hatte bislang in der Einzelerfassung ueberhaupt
 * kein Formular (nur die Massenerfassung kannte es), bekommt hier also
 * additiv das generische Formular statt weiter unerreichbar zu bleiben.
 */
const BEREICHE_MIT_SPEZIALFORMULAR = new Set(['TUER']);

const BEREICH_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  TUER: DoorOpen,
  SCHOTT: ShieldCheck,
  FEUERLOESCHER: Flame,
  RWA: Wind,
  BMA: Bell,
  SPRINKLER: Droplets,
};

enum WizardStep {
  SELECT_KUNDE,
  SELECT_STANDORT,
  SELECT_STOCKWERK,
  MAIN_VIEW
}

type CaptureMode = 'single' | 'batch';

/**
 * Erlaubte Werte in der Massenerfassung. Bewusst auf die Bauteile
 * beschraenkt, die sich sinnvoll ohne technische Zusatzfelder anlegen
 * lassen -- Feuerloescher/RWA/BMA/Sprinkler haben eigene Pflichtfelder
 * (siehe pruefbereicheKatalog.ts) und gehoeren in die Einzelerfassung
 * (DoorCaptureWizard), nicht in die schnelle CSV-Massenanlage.
 */
const BATCH_TYPEN: ElementTyp[] = ['TÜR', 'SCHOTT'];

interface BatchElement {
  id: string;
  typ: ElementTyp;
  element_id_kunde: string;
  raum: string;
  status: string;
  barcode?: string;
}

export default function Erfassung() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<WizardStep>(WizardStep.SELECT_KUNDE);
  const [selectedKunde, setSelectedKunde] = useState<any>(null);
  const [selectedStandort, setSelectedStandort] = useState<any>(null);
  const [selectedStockwerk, setSelectedStockwerk] = useState<any>(null);
  const [selectedProjekt, setSelectedProjekt] = useState<ProjektWithDetails | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [editingElement, setEditingElement] = useState<any>(null);
  const [captureMode, setCaptureMode] = useState<CaptureMode>('single');

  // Prüfbereich-Auswahl vor der Erfassung (Phase 6, Lücke 2)
  const [showBereichAuswahl, setShowBereichAuswahl] = useState(false);
  const [genericCaptureBereich, setGenericCaptureBereich] = useState<Pruefbereich | null>(null);

  // Batch mode state
  const [batchElements, setBatchElements] = useState<BatchElement[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [showImportResult, setShowImportResult] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; errors: string[] } | null>(null);

  // Queries
  const { data: kunden = [] } = useQuery({ queryKey: ['kunden'], queryFn: kundenApi.getAll });
  const { data: standorte = [] } = useQuery({
    queryKey: ['standorte', selectedKunde?.id],
    queryFn: () => standorteApi.getAll(selectedKunde?.id),
    enabled: !!selectedKunde
  });

  // Ergebnistyp explizit angeben: gebaeudeApi.getAll ist untypisiert, ohne
  // Generic verliert der Destrukturierungs-Default den Elementtyp und jedes
  // g in einem map/find waere implizit any.
  const { data: gebaeude = [] } = useQuery<any[]>({
    queryKey: ['gebaeude', selectedStandort?.id],
    queryFn: () => gebaeudeApi.getAll(selectedStandort?.id),
    enabled: !!selectedStandort
  });

  const { data: allStockwerke = [] } = useQuery({
    queryKey: ['stockwerke', gebaeude.map(g => g.id)],
    queryFn: async () => {
      const results = await Promise.all(gebaeude.map(g => stockwerkeApi.getAll(g.id)));
      return results.flat();
    },
    enabled: gebaeude.length > 0
  });

  const { data: elements = [], refetch: refetchElements } = useQuery({
    queryKey: ['elements', selectedStockwerk?.id],
    queryFn: () => elementeApi.getAll(selectedStockwerk?.id),
    enabled: !!selectedStockwerk
  });

  // Prüfbereiche für die Bereichsauswahl (Phase 6, Lücke 2)
  const { data: pruefbereiche = [] } = useQuery<Pruefbereich[]>({
    queryKey: ['pruefbereiche'],
    queryFn: () => pruefbereicheApi.liste(),
  });

  // Aktive Projekte für den gewählten Kunden
  const { data: aktiveProjekte = [] } = useQuery<ProjektWithDetails[]>({
    queryKey: ['projekte', 'kunde', selectedKunde?.id],
    queryFn: () => selectedKunde?.id ? projekteApi.getByKunde(selectedKunde.id) : projekteApi.getAll(),
    enabled: !!selectedKunde,
    select: (data) => data.filter((p: ProjektWithDetails) => p.status === 'AKTIV' || p.status === 'ENTWURF')
  });

  // Batch create mutation with project assignment
  const batchCreateMutation = useMutation({
    mutationFn: async (elements: any[]) => {
      const result = await elementeApi.createBatch(elements);
      // Wenn ein Projekt ausgewählt ist, füge die Elemente hinzu
      if (selectedProjekt?.id && result.created > 0) {
        try {
          // Hole die zuletzt erstellten Elemente
          const allElements = await elementeApi.getAll(selectedStockwerk?.id);
          const lastCreatedIds = allElements
            .slice(-result.created)
            .map((el: any) => el.id);
          // Füge sie zum Projekt hinzu
          await projekteApi.addElementeBatch(selectedProjekt.id, lastCreatedIds);
        } catch (err) {
          log.error('Fehler beim Hinzufügen zum Projekt:', err);
        }
      }
      return result;
    },
    onSuccess: (data) => {
      setImportResult(data);
      setShowImportResult(true);
      setBatchElements([]);
      refetchElements();
      queryClient.invalidateQueries({ queryKey: ['elements'] });
      queryClient.invalidateQueries({ queryKey: ['projekte'] });
    },
    onError: (error: any) => {
      setImportErrors([error.message || 'Fehler beim Import']);
    }
  });

  // Handlers
  const handleSelectKunde = (k: any) => {
    setSelectedKunde(k);
    setStep(WizardStep.SELECT_STANDORT);
  };

  const handleSelectStandort = (s: any) => {
    setSelectedStandort(s);
    setStep(WizardStep.SELECT_STOCKWERK);
  };

  const handleSelectStockwerk = (sw: any) => {
    setSelectedStockwerk(sw);
    setStep(WizardStep.MAIN_VIEW);
  };

  // Batch mode handlers
  const addBatchElement = () => {
    setBatchElements([
      ...batchElements,
      {
        id: crypto.randomUUID(),
        typ: 'TÜR',
        element_id_kunde: '',
        raum: '',
        status: 'NICHT_GEPRUEFT'
      }
    ]);
  };

  const updateBatchElement = (id: string, field: keyof BatchElement, value: string) => {
    setBatchElements(batchElements.map(el =>
      el.id === id ? { ...el, [field]: value } : el
    ));
  };

  const removeBatchElement = (id: string) => {
    setBatchElements(batchElements.filter(el => el.id !== id));
  };

  const handleBatchSubmit = () => {
    if (batchElements.length === 0 || !selectedStockwerk) return;

    const elementsToCreate = batchElements.map(el => ({
      stockwerk_id: selectedStockwerk.id,
      typ: el.typ,
      element_id_kunde: el.element_id_kunde || undefined,
      raum: el.raum || undefined,
      status: el.status,
      barcode: el.barcode || undefined
    }));

    batchCreateMutation.mutate(elementsToCreate);
  };

  const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        setImportErrors(['CSV-Datei ist leer oder enthält nur Header']);
        return;
      }

      const header = lines[0].split(';').map(h => h.trim().toLowerCase());
      const requiredFields = ['typ'];
      const missingFields = requiredFields.filter(f => !header.includes(f));

      if (missingFields.length > 0) {
        setImportErrors([`Fehlende Pflichtfelder: ${missingFields.join(', ')}`]);
        return;
      }

      const newElements: BatchElement[] = [];
      const errors: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';').map(v => v.trim());
        const row: Record<string, string> = {};

        header.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });

        const typ = row.typ?.toUpperCase();
        if (typ !== 'TÜR' && typ !== 'SCHOTT') {
          errors.push(`Zeile ${i + 1}: Ungültiger Typ "${row.typ}" (erlaubt: TÜR, SCHOTT)`);
          continue;
        }

        newElements.push({
          id: crypto.randomUUID(),
          typ: typ as 'TÜR' | 'SCHOTT',
          element_id_kunde: row.element_id_kunde || row.kunden_id || '',
          raum: row.raum || '',
          status: row.status || 'NICHT_GEPRUEFT',
          barcode: row.barcode || ''
        });
      }

      if (errors.length > 0) {
        setImportErrors(errors);
      } else {
        setImportErrors([]);
      }

      setBatchElements(prev => [...prev, ...newElements]);
    };

    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadCSVTemplate = () => {
    const template = 'typ;element_id_kunde;raum;status;barcode\nTÜR;K-001;Raum 101;NICHT_GEPRUEFT;\nSCHOTT;K-002;Flur EG;NICHT_GEPRUEFT;';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'elemente_vorlage.csv';
    link.click();
  };

  return (
    <div className="max-w-6xl mx-auto pb-6 px-2">
      {/* Header & Breadcrumbs */}
      <div className="py-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white shadow-sm">
            <DoorOpen size={16} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-secondary-900 tracking-tight">Erfassung</h1>
            <p className="text-secondary-500 text-xs">BRANTAS</p>
          </div>
        </div>

        {/* Dynamic Breadcrumbs / Progress - Wizard Steps */}
        <div className="flex items-center gap-0.5 p-1 bg-secondary-100/50 rounded-lg border border-secondary-200/60 overflow-x-auto no-scrollbar">
          {/* Step 1: Kunde */}
          <button
            onClick={() => setStep(WizardStep.SELECT_KUNDE)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-all whitespace-nowrap ${
              step === WizardStep.SELECT_KUNDE
                ? 'bg-primary-600 text-white'
                : selectedKunde
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'text-secondary-500 hover:bg-secondary-200'
            }`}
          >
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-black ${
              step === WizardStep.SELECT_KUNDE ? 'bg-white/20' : selectedKunde ? 'bg-emerald-200' : 'bg-secondary-200'
            }`}>1</span>
            <Users size={10} />
            <span className="hidden sm:inline truncate max-w-[60px]">{selectedKunde?.name || 'Kunde'}</span>
          </button>

          <ChevronRight size={10} className="text-secondary-300 flex-shrink-0" />

          {/* Step 2: Standort */}
          <button
            disabled={!selectedKunde}
            onClick={() => setStep(WizardStep.SELECT_STANDORT)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-all whitespace-nowrap ${
              step === WizardStep.SELECT_STANDORT
                ? 'bg-primary-600 text-white'
                : selectedStandort
                  ? 'bg-emerald-100 text-emerald-700'
                  : selectedKunde
                    ? 'text-secondary-600 hover:bg-secondary-200'
                    : 'text-secondary-300 pointer-events-none'
            }`}
          >
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-black ${
              step === WizardStep.SELECT_STANDORT ? 'bg-white/20' : selectedStandort ? 'bg-emerald-200' : 'bg-secondary-200'
            }`}>2</span>
            <MapPin size={10} />
            <span className="hidden sm:inline truncate max-w-[60px]">{selectedStandort?.name || 'Standort'}</span>
          </button>

          <ChevronRight size={10} className="text-secondary-300 flex-shrink-0" />

          {/* Step 3: Stockwerk */}
          <button
            disabled={!selectedStandort}
            onClick={() => setStep(WizardStep.SELECT_STOCKWERK)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-all whitespace-nowrap ${
              step === WizardStep.SELECT_STOCKWERK
                ? 'bg-primary-600 text-white'
                : selectedStockwerk
                  ? 'bg-emerald-100 text-emerald-700'
                  : selectedStandort
                    ? 'text-secondary-600 hover:bg-secondary-200'
                    : 'text-secondary-300 pointer-events-none'
            }`}
          >
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-black ${
              step === WizardStep.SELECT_STOCKWERK ? 'bg-white/20' : selectedStockwerk ? 'bg-emerald-200' : 'bg-secondary-200'
            }`}>3</span>
            <Layers size={10} />
            <span className="hidden sm:inline truncate max-w-[60px]">{selectedStockwerk?.name || 'Etage'}</span>
          </button>

          <ChevronRight size={10} className="text-secondary-300 flex-shrink-0" />

          {/* Step 4: Erfassung */}
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold whitespace-nowrap ${
              step === WizardStep.MAIN_VIEW
                ? 'bg-primary-600 text-white'
                : selectedStockwerk
                  ? 'text-secondary-600'
                  : 'text-secondary-300'
            }`}
          >
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-black ${
              step === WizardStep.MAIN_VIEW ? 'bg-white/20' : 'bg-secondary-200'
            }`}>4</span>
            <DoorOpen size={10} />
            <span className="hidden sm:inline">Erfassung</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">

        {/* STEP 1: KUNDE */}
        {step === WizardStep.SELECT_KUNDE && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {kunden.map((k: any) => (
              <button
                key={k.id}
                onClick={() => handleSelectKunde(k)}
                className="p-2.5 bg-white border border-secondary-100 rounded-lg text-left hover:border-primary-500 hover:shadow-md transition-all group active:scale-[0.98]"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 bg-secondary-100 rounded flex items-center justify-center text-secondary-600 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                    <Users size={12} />
                  </div>
                  <div className="w-5 h-5 bg-secondary-50 rounded-full flex items-center justify-center text-secondary-400 group-hover:bg-primary-600 group-hover:text-white transition-all ml-auto">
                    <ChevronRight size={10} />
                  </div>
                </div>
                <h3 className="text-xs font-bold text-secondary-900 truncate">{k.name}</h3>
                <p className="text-secondary-500 text-xs truncate">{k.ansprechpartner || '-'}</p>
              </button>
            ))}
          </div>
        )}

        {/* STEP 2: STANDORT */}
        {step === WizardStep.SELECT_STANDORT && (
          <div className="space-y-2">
            <button
              onClick={() => setStep(WizardStep.SELECT_KUNDE)}
              className="flex items-center gap-1 text-secondary-500 text-xs font-bold hover:text-primary-600 transition-colors"
            >
              <ArrowLeft size={12} /> Zurück
            </button>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {standorte.map((s: any) => (
                <button
                  key={s.id}
                  onClick={() => handleSelectStandort(s)}
                  className="p-2.5 bg-white border border-secondary-100 rounded-lg text-left hover:border-primary-500 hover:shadow-md transition-all group active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 bg-secondary-100 rounded flex items-center justify-center text-secondary-600 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                      <MapPin size={12} />
                    </div>
                    <div className="w-5 h-5 bg-secondary-50 rounded-full flex items-center justify-center text-secondary-400 group-hover:bg-primary-600 group-hover:text-white transition-all ml-auto">
                      <ChevronRight size={10} />
                    </div>
                  </div>
                  <h3 className="text-xs font-bold text-secondary-900 truncate">{s.name}</h3>
                  <p className="text-secondary-500 text-xs truncate">{s.adresse || '-'}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: STOCKWERK */}
        {step === WizardStep.SELECT_STOCKWERK && (
          <div className="space-y-2">
            <button
              onClick={() => setStep(WizardStep.SELECT_STANDORT)}
              className="flex items-center gap-1 text-secondary-500 text-xs font-bold hover:text-primary-600 transition-colors"
            >
              <ArrowLeft size={12} /> Zurück
            </button>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {allStockwerke.map((sw: any) => (
                <button
                  key={sw.id}
                  onClick={() => handleSelectStockwerk(sw)}
                  className="p-2.5 bg-white border border-secondary-100 rounded-lg text-left hover:border-primary-500 hover:shadow-md transition-all group active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 bg-secondary-100 rounded flex items-center justify-center text-secondary-600 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                      <Layers size={12} />
                    </div>
                    <div className="w-5 h-5 bg-secondary-50 rounded-full flex items-center justify-center text-secondary-400 group-hover:bg-primary-600 group-hover:text-white transition-all ml-auto">
                      <ChevronRight size={10} />
                    </div>
                  </div>
                  <h3 className="text-xs font-bold text-secondary-900 truncate">{sw.name}</h3>
                  <p className="text-secondary-500 text-xs truncate">{gebaeude.find(g => g.id === sw.gebaeude_id)?.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 4: MAIN VIEW (List & Capture Trigger) */}
        {step === WizardStep.MAIN_VIEW && (
          <div className="space-y-2 animate-in fade-in zoom-in-95 duration-500">
            {/* Projekt-Auswahl (optional) */}
            {aktiveProjekte.length > 0 && (
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-3 rounded-lg border border-primary-200 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FolderKanban size={16} className="text-primary-600" />
                    <span className="text-xs font-medium text-secondary-700">Projekt-Zuordnung:</span>
                  </div>
                  <select
                    value={selectedProjekt?.id || ''}
                    onChange={(e) => {
                      const projektId = e.target.value ? parseInt(e.target.value) : null;
                      const projekt = aktiveProjekte.find((p: ProjektWithDetails) => p.id === projektId) || null;
                      setSelectedProjekt(projekt);
                    }}
                    className="flex-1 max-w-xs text-xs py-1.5 px-2 border border-primary-200 rounded-lg bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Ohne Projekt erfassen</option>
                    {aktiveProjekte.map((p: ProjektWithDetails) => (
                      <option key={p.id} value={p.id}>
                        {p.projekt_nr} - {p.bezeichnung}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedProjekt && (
                  <div className="mt-2 pt-2 border-t border-primary-200 flex items-center gap-2 text-xs text-primary-700">
                    <CheckCircle2 size={12} className="text-green-600" />
                    <span>Neue Elemente werden automatisch zu <strong>{selectedProjekt.bezeichnung}</strong> hinzugefügt</span>
                  </div>
                )}
              </div>
            )}

            {/* Mode Toggle & Actions */}
            <div className="bg-white p-2.5 rounded-lg border border-secondary-200 shadow-sm">
              <div className="flex justify-between items-center gap-2">
                <div className="flex items-center gap-0.5 p-0.5 bg-secondary-100 rounded">
                  <button
                    onClick={() => setCaptureMode('single')}
                    className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${
                      captureMode === 'single'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-secondary-500 hover:text-secondary-700'
                    }`}
                  >
                    <PlusCircle size={12} /> Einzeln
                  </button>
                  <button
                    onClick={() => setCaptureMode('batch')}
                    className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${
                      captureMode === 'batch'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-secondary-500 hover:text-secondary-700'
                    }`}
                  >
                    <FileSpreadsheet size={12} /> Masse
                  </button>
                </div>
                <span className="text-xs text-secondary-500">{selectedStockwerk?.name}</span>
              </div>
            </div>

            {/* Single Mode: Original Flow */}
            {captureMode === 'single' && (
              <>
                {/* Action Bar */}
                <div className="bg-white p-2.5 rounded-lg border border-secondary-200 shadow-sm flex justify-between items-center gap-2">
                  <div className="text-xs text-secondary-600">
                    <span className="font-bold">{elements.length}</span> Elemente
                  </div>
                  <button
                    onClick={() => setShowBereichAuswahl(true)}
                    className="px-3 py-1.5 bg-primary-600 text-white rounded-lg font-bold text-xs shadow-sm hover:bg-primary-700 active:scale-[0.98] transition-all flex items-center gap-1.5"
                  >
                    <PlusCircle size={14} />
                    Neu erfassen
                  </button>
                </div>

                {/* Existing Elements List */}
                <div className="grid grid-cols-1 gap-1.5">
                  {elements.length === 0 ? (
                    <div className="p-6 text-center bg-secondary-50 rounded-lg border-2 border-dashed border-secondary-200">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mx-auto text-secondary-300 shadow-sm mb-2">
                        <Search size={16} />
                      </div>
                      <h3 className="text-sm font-bold text-secondary-900">Keine Elemente</h3>
                      <p className="text-secondary-500 text-xs">Starten Sie mit der ersten Erfassung</p>
                    </div>
                  ) : (
                    elements.map((el: any) => (
                      <div key={el.id} className="p-2 bg-white border border-secondary-100 rounded-lg flex items-center justify-between hover:border-primary-200 transition-all shadow-sm">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded flex items-center justify-center ${
                            el.typ === 'TÜR' ? 'bg-purple-50 text-purple-600' : 'bg-indigo-50 text-indigo-600'
                          }`}>
                            {el.typ === 'TÜR' ? <DoorOpen size={14} /> : <ShieldCheck size={14} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-[11px] font-bold bg-secondary-100 px-1 py-0.5 rounded text-secondary-600">{el.element_id_intern || 'XXX'}</span>
                              <h4 className="text-xs font-bold text-secondary-900">{el.element_id_kunde || 'Unbenannt'}</h4>
                            </div>
                            <p className="text-secondary-500 text-xs">{el.typ} • {el.raum || '-'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                            el.status === 'i.O.' ? 'bg-emerald-50 text-emerald-600' :
                            el.status === 'n.i.O.' || el.status === 'Mangel' ? 'bg-rose-50 text-rose-600' :
                            'bg-secondary-50 text-secondary-600'
                          }`}>
                            {el.status}
                          </span>
                          <button
                            onClick={() => {
                              // Phase 6, Luecke 1: Bearbeiten routet je nach
                              // Bereich zum passenden Wizard. TÜR hat ein
                              // eigenes Formular (DoorCaptureWizard); alle
                              // anderen Bereiche -- inklusive SCHOTT -- nutzen
                              // GenericElementCaptureWizard im Bearbeiten-Modus.
                              const schluessel = schluesselFuerElementTyp(el.typ);
                              if (schluessel === 'TUER') {
                                setEditingElement(el);
                                setIsCapturing(true);
                                return;
                              }
                              const bereich = pruefbereiche.find((b) => b.schluessel === schluessel);
                              if (bereich) {
                                setEditingElement(el);
                                setGenericCaptureBereich(bereich);
                              } else {
                                // Kein Katalogeintrag (mehr) fuer diesen
                                // Schluessel gefunden -- fachlich zu klaeren,
                                // statt ein Element ohne Formular zu oeffnen.
                                setEditingElement(el);
                                setIsCapturing(true);
                              }
                            }}
                            className="p-1.5 bg-secondary-50 text-secondary-400 rounded hover:bg-primary-50 hover:text-primary-600 transition-all"
                            title="Bearbeiten"
                          >
                            <Info size={12} />
                          </button>
                          <button className="p-1.5 bg-secondary-50 text-secondary-400 rounded hover:bg-rose-50 hover:text-rose-600 transition-all" title="Löschen">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Batch Mode */}
            {captureMode === 'batch' && (
              <div className="space-y-2">
                {/* Batch Controls */}
                <div className="bg-white p-2 rounded-lg border border-secondary-200 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={addBatchElement}
                        className="btn btn-primary px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <Plus size={12} /> Zeile
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCSVImport}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <Upload size={12} /> CSV
                      </button>
                      <button
                        onClick={downloadCSVTemplate}
                        className="btn btn-secondary px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <Download size={12} /> Vorlage
                      </button>
                    </div>

                    {batchElements.length > 0 && (
                      <button
                        onClick={handleBatchSubmit}
                        disabled={batchCreateMutation.isPending}
                        className="btn btn-primary px-2 py-1 text-xs flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <CheckCircle2 size={12} />
                        {batchCreateMutation.isPending ? '...' : `${batchElements.length} speichern`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Import Errors */}
                {importErrors.length > 0 && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-1.5">
                      <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={12} />
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-red-700">Warnungen</h4>
                        <ul className="text-xs text-red-600 mt-0.5">
                          {importErrors.slice(0,3).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                      <button onClick={() => setImportErrors([])} className="p-0.5 hover:bg-red-100 rounded">
                        <X size={12} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Batch Table */}
                {batchElements.length > 0 ? (
                  <div className="bg-white rounded-lg border border-secondary-200 shadow-sm overflow-hidden">
                    <div className="p-1.5 bg-secondary-50 border-b border-secondary-200">
                      <div className="grid grid-cols-12 gap-1 text-[11px] font-bold text-secondary-500 uppercase">
                        <div className="col-span-2">Typ</div>
                        <div className="col-span-3">ID</div>
                        <div className="col-span-3">Raum</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-1">BC</div>
                        <div className="col-span-1"></div>
                      </div>
                    </div>
                    <div className="divide-y divide-secondary-100 max-h-[250px] overflow-y-auto">
                      {batchElements.map((el, _index) => (
                        <div key={el.id} className="p-1.5 hover:bg-secondary-50/50 transition-colors">
                          <div className="grid grid-cols-12 gap-1 items-center">
                            <div className="col-span-2">
                              <select
                                value={el.typ}
                                onChange={(e) => updateBatchElement(el.id, 'typ', e.target.value)}
                                className="input px-2 py-1 text-xs"
                              >
                                {BATCH_TYPEN.map((t) => (
                                  <option key={t} value={t}>{TYP_LABELS[t]?.label ?? t}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-span-3">
                              <input
                                type="text"
                                value={el.element_id_kunde}
                                onChange={(e) => updateBatchElement(el.id, 'element_id_kunde', e.target.value)}
                                className="input px-2 py-1 text-xs"
                                placeholder="K-001"
                              />
                            </div>
                            <div className="col-span-3">
                              <input
                                type="text"
                                value={el.raum}
                                onChange={(e) => updateBatchElement(el.id, 'raum', e.target.value)}
                                className="input px-2 py-1 text-xs"
                                placeholder="Raum"
                              />
                            </div>
                            <div className="col-span-2">
                              <select
                                value={el.status}
                                onChange={(e) => updateBatchElement(el.id, 'status', e.target.value)}
                                className="input px-2 py-1 text-xs"
                              >
                                <option value="NICHT_GEPRUEFT">Offen</option>
                                <option value="i.O.">i.O.</option>
                                <option value="n.i.O.">n.i.O.</option>
                              </select>
                            </div>
                            <div className="col-span-1">
                              <input
                                type="text"
                                value={el.barcode || ''}
                                onChange={(e) => updateBatchElement(el.id, 'barcode', e.target.value)}
                                className="input px-2 py-1 text-xs"
                                placeholder="BC"
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                onClick={() => removeBatchElement(el.id)}
                                className="p-1 hover:bg-red-50 text-secondary-400 hover:text-red-500 rounded transition-colors"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center bg-secondary-50 rounded-lg border-2 border-dashed border-secondary-200">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mx-auto text-secondary-300 shadow-sm mb-2">
                      <FileSpreadsheet size={16} />
                    </div>
                    <h3 className="text-sm font-bold text-secondary-900">Massenmodus</h3>
                    <p className="text-secondary-500 text-xs">Zeilen hinzufügen oder CSV importieren</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Prüfbereich-Auswahl (Phase 6, Lücke 2) */}
      {showBereichAuswahl && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm animate-in zoom-in-95 duration-200">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-secondary-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-secondary-900">Neue Prüfung</h3>
                <p className="text-sm text-secondary-500 font-medium">Prüfbereich wählen</p>
              </div>
              <button
                onClick={() => setShowBereichAuswahl(false)}
                className="p-2 hover:bg-secondary-100 rounded-xl text-secondary-400 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto grid grid-cols-2 gap-3">
              {pruefbereiche.map((bereich) => {
                const Icon = BEREICH_ICONS[bereich.schluessel] || ShieldCheck;
                return (
                  <button
                    key={bereich.id}
                    onClick={() => {
                      setShowBereichAuswahl(false);
                      setEditingElement(null);
                      if (BEREICHE_MIT_SPEZIALFORMULAR.has(bereich.schluessel)) {
                        setIsCapturing(true);
                      } else {
                        setGenericCaptureBereich(bereich);
                      }
                    }}
                    className="p-5 bg-secondary-50 hover:bg-primary-50 border-4 border-secondary-100 hover:border-primary-300 rounded-3xl flex flex-col items-center gap-2 text-center transition-all active:scale-[0.97]"
                  >
                    <Icon size={28} />
                    <span className="font-black text-sm text-secondary-900">{bereich.bezeichnung}</span>
                  </button>
                );
              })}
              {pruefbereiche.length === 0 && (
                <div className="col-span-2 p-6 text-center text-secondary-400 text-sm font-medium">
                  Keine Prüfbereiche verfügbar.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Door Capture Overlay (Tür — spezialisiertes Formular) */}
      {isCapturing && (
        <DoorCaptureWizard
          stockwerkId={selectedStockwerk?.id}
          stockwerkName={selectedStockwerk?.name}
          editElement={editingElement}
          onClose={() => {
            setIsCapturing(false);
            setEditingElement(null);
            refetchElements();
          }}
        />
      )}

      {/* Generisches Erfassungsformular (Schott + neue Prüfbereiche) */}
      {genericCaptureBereich && selectedStockwerk && (
        <GenericElementCaptureWizard
          stockwerkId={selectedStockwerk.id}
          stockwerkName={selectedStockwerk.name}
          bereich={genericCaptureBereich}
          element={editingElement}
          onClose={() => {
            setGenericCaptureBereich(null);
            setEditingElement(null);
            refetchElements();
          }}
        />
      )}

      {/* Import Result Modal */}
      {showImportResult && importResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm animate-in zoom-in-95 duration-200">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="p-5 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={24} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-secondary-900 mb-1">Import erfolgreich</h3>
              <p className="text-secondary-600 text-sm mb-4">
                <span className="font-bold text-emerald-600">{importResult.created}</span> Elemente erstellt
              </p>
              {importResult.errors.length > 0 && (
                <div className="text-left p-3 bg-amber-50 rounded-lg mb-4">
                  <h4 className="text-sm font-bold text-amber-700 mb-1">Warnungen:</h4>
                  <ul className="text-xs text-amber-600 space-y-0.5">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={() => setShowImportResult(false)}
                className="btn btn-primary w-full py-2.5 text-sm"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
